import { ContractStatus } from 'components/ContractStatus';
import type { ITabItem } from 'components/Form/ControlledTab';
import { ReadField } from 'components/ReadField';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { Viewport } from 'components/Viewport';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    ProjectContractTypeEnum,
    BimContractResultsViewData,
    BimNormalContractResultData,
    BimContractData,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { useRemainderPie } from 'pages/Project/Common/hooks/useRemainderPie';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import type { TabsIds } from 'pages/Project/Common/types';
import { contractTabs, getProjectContractTypeTitle } from 'pages/Project/Common/utils';
import type { DeepPartial } from 'types';
import { cnMixSpace, Tabs, Grid, GridItem, Pie, Collapse } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { BimContractAccumulationSheet } from '../../BimContractAccumulationSheet/BimContractAccumulationSheet';
import { mapResultPlan } from '../../hooks/useBimContractFormData';
import { useBimFrameContractResults } from '../../hooks/useBimFrameContractResults';
import { useBimNormalContractResults } from '../../hooks/useBimNormalContractResults';

import { BimContractReadCommonForm } from './BimContractReadCommonForm';
import { BimContractReadPriceForm } from './BimContractReadPriceForm';

import Style from './BimContractReadForm.module.css';

const PieStyle = { width: 250, height: 170 };

interface IBimContractReadForm {
    data: BimContractData;
    isPieShow: boolean;
    contractType: ProjectContractTypeEnum;
    contractId: number;
    isFrameContractWithWorkOrders: boolean;
    isNormalContractWithCalculation: boolean;
    contractCalculationStatus?: WorkOrderStatusEnum;
}

export const BimContractReadForm: FC<IBimContractReadForm> = (props) => {
    const {
        data,
        isPieShow,
        contractType,
        contractId,
        isFrameContractWithWorkOrders,
        isNormalContractWithCalculation,
        contractCalculationStatus,
    } = props;

    const { status, authoredBy, constructionTypes, seasons } = data || {};
    const [isOpen, setOpen] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<ITabItem<TabsIds>>(contractTabs[0]);
    const [frameContractResults, setFrameContractResults] = useState<BimContractResultsViewData | undefined | null>();
    const [normalContractResults, setNormalContractResults] = useState<
        (BimNormalContractResultData | null)[] | undefined | null
    >();

    const { isHeaderOpened: isContextHeaderOpen } = useContext(PageHeaderLayoutContext);

    const { pieData, pieColor, pieStatistic, pieTooltip } = useRemainderPie(data?.cost);

    const { bimFrameContractResultsStatus, bimFrameContractResultsRequest } = useBimFrameContractResults(
        contractId,
        setFrameContractResults as Dispatch<SetStateAction<DeepPartial<BimContractResultsViewData> | undefined | null>>,
    );

    const { bimNormalContractResultsStatus, bimNormalContractResultsRequest } = useBimNormalContractResults(
        contractId,
        setNormalContractResults,
    );

    const isAccumulationSheetLoading = useMemo(
        () =>
            bimFrameContractResultsStatus === RequestStatus.FETCHING ||
            bimNormalContractResultsStatus === RequestStatus.FETCHING,
        [bimFrameContractResultsStatus, bimNormalContractResultsStatus],
    );

    const pieRef = useRef<HTMLDivElement>(null);

    const constrTypes = useMemo(
        () =>
            constructionTypes
                ?.map((t) => t.name)
                .sort()
                .join(', '),
        [constructionTypes],
    );

    const seasonsRange = useMemo(() => {
        const years = seasons?.map((s) => Number(s.year));
        const len = years?.length;

        if (len) {
            if (len === 1) {
                return years[0];
            }
            const min = Math.min(...years);
            const max = Math.max(...years);

            return `${min} - ${max}`;
        }

        return '';
    }, [seasons]);

    const contractTitle = useMemo(
        () => `Договор${descriptionNumberAndDate(data?.generalInfo?.internalNumber, data?.generalInfo?.signingDate)}`,
        [data?.generalInfo?.internalNumber, data?.generalInfo?.signingDate],
    );

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    useEffect(() => {
        if (isFrameContractWithWorkOrders && !frameContractResults) {
            bimFrameContractResultsRequest();
        }

        if (isNormalContractWithCalculation && !normalContractResults) {
            bimNormalContractResultsRequest();
        }
    }, [
        bimFrameContractResultsRequest,
        bimNormalContractResultsRequest,
        frameContractResults,
        isFrameContractWithWorkOrders,
        isNormalContractWithCalculation,
        normalContractResults,
    ]);

    const heightContext = !isContextHeaderOpen ? 140 : 0;
    const heightOpenCollapse = isOpen ? `${170 - heightContext}px` : `${0 - heightContext}px`;

    return (
        <>
            <div className={cnMixSpace({ mB: 's' })}>
                <Collapse label="Основная информация" isOpen={isOpen} onClick={() => setOpen(!isOpen)} size="s">
                    <div style={{ display: 'flex', overflow: 'hidden' }}>
                        <div>
                            <ReadField
                                value={status}
                                label="Статус"
                                render={(value) => <ContractStatus id={value} />}
                            />
                            <ReadField value={authoredBy} label="Автор" />
                            <ReadField value={getProjectContractTypeTitle(contractType)} label="Тип договора" />
                            <ReadField value={constrTypes} label="Виды строительства" />
                            <ReadField value={seasonsRange} label="Сезоны" />
                        </div>

                        {isPieShow && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    position: 'relative',
                                }}
                            >
                                <div className={Style.wrapperPie}>
                                    <TooltipWrapper
                                        direction="downCenter"
                                        message={pieTooltip}
                                        isHidden={!pieTooltip || !isOpen}
                                        toolTipClassName={Style.wrapperPieTooltip}
                                    >
                                        <Pie
                                            ref={pieRef}
                                            angleField="value"
                                            colorField="type"
                                            data={pieData}
                                            color={pieColor}
                                            innerRadius={0.75}
                                            label={undefined}
                                            legend={false}
                                            tooltip={false}
                                            animation={false}
                                            statistic={pieStatistic}
                                            style={PieStyle}
                                        />
                                    </TooltipWrapper>
                                </div>
                            </div>
                        )}
                    </div>
                </Collapse>
            </div>
            <Grid rowGap="xl" cols="1" style={{ height: 36 }}>
                <GridItem>
                    <Tabs
                        value={activeTab}
                        onChange={onTabChange}
                        items={contractTabs}
                        getItemLabel={(item) => item.label}
                        view="bordered"
                        size="s"
                    />
                </GridItem>
            </Grid>
            <Viewport
                topIndention={heightOpenCollapse}
                marginBottom="var(--space-margin-bottom)"
                shownChildIndex={activeTab.id}
            >
                <BimContractReadCommonForm data={data} />
                <BimContractReadPriceForm data={data} />
                <BimContractAccumulationSheet
                    constructionTypes={data?.constructionTypes}
                    resultPlan={mapResultPlan(data?.resultPlan)}
                    contractTitle={contractTitle}
                    frameContractResults={frameContractResults}
                    normalContractResults={normalContractResults}
                    isLoading={isAccumulationSheetLoading}
                    hasNoPlanByConstructionType={data?.hasNoPlanByConstructionType}
                    contractCalculationStatus={contractCalculationStatus}
                    isReadOnly
                />
            </Viewport>
        </>
    );
};

BimContractReadForm.displayName = 'BimContractReadForm';
